import React, { useState, useRef, useEffect } from 'react';

interface ScrollingTextProps {
  text: string;
  className?: string;
}

// ScrollingText component for marquee-like effect
const ScrollingText: React.FC<ScrollingTextProps> = ({ text, className = "" }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [animationDuration, setAnimationDuration] = useState("10s");
  const [key, setKey] = useState(0); // Used to force animation restart

  // Force remeasure when component mounts and when text changes
  useEffect(() => {
    // Delay measurement slightly to ensure DOM is ready
    const timer = setTimeout(() => {
      if (containerRef.current && textRef.current) {
        const textWidth = textRef.current.scrollWidth;
        const containerWidth = containerRef.current.clientWidth;
        
        // Set to scroll if text is wider than container
        const needsScroll = textWidth > containerWidth;
        setShouldScroll(needsScroll);
        
        // Calculate animation duration based on text length
        // Longer text should move slower for readability
        if (needsScroll) {
          const duration = Math.max(8, textWidth / 20);
          setAnimationDuration(`${duration}s`);
        }
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [text]);

  // Reset animation when it completes
  useEffect(() => {
    if (!shouldScroll) return;
    
    const duration = parseFloat(animationDuration);
    const timer = setTimeout(() => {
      setKey(prev => prev + 1); // Update key to trigger animation restart
    }, duration * 1000);
    
    return () => clearTimeout(timer);
  }, [shouldScroll, animationDuration, key]);

  return (
    <div ref={containerRef} className={`text-container ${className}`}>
      {shouldScroll ? (
        <div 
          key={key}
          className="animate-scrolling-text"
          style={{
            animationDuration,
          }}
          data-text={text}
        >
          <span ref={textRef}>{text}</span>
        </div>
      ) : (
        <div ref={textRef}>{text}</div>
      )}
    </div>
  );
};

export default ScrollingText; 